/**
 * A box's volatility: boxed's spiciness (`boxWeighting/spiciness.ts` in boxed-backend), how swingy
 * an open is. It is one over the chance of rolling an entry worth at least the box's expected
 * value: a box where one roll in ten beats its EV reads 10, a box whose entries are all worth the
 * same reads 1. The server computes it when it writes a version (`box_versions.volatility`); the
 * forge builder is to compute it live from the rows it is editing with the same function, so a
 * preview and the published box agree. Read on a scale from 0 to 16 in four even levels; the raw
 * number runs past 16 (a one-in-10,000 chase reads 10,000), and anything above reads as max.
 */

/** The top of the scale: a value at or above it reads as the top of the last level. */
export const VOLATILITY_MAX = 16;

/** The levels, mildest first: one per quarter of the scale. */
export const VOLATILITY_LEVELS = ["low", "medium", "high", "max"] as const;
export type VolatilityLevel = (typeof VOLATILITY_LEVELS)[number];

/** One entry of a drop table as volatility reads it. */
export interface VolatilityEntry {
  /** The entry's weight; any scale, since only the shares matter. 0 is an entry that can't drop. */
  weight: number;
  /** Integer cents: what the entry is worth, the value the box's EV is computed from. */
  value: number;
}

/**
 * The drop table's volatility, or null when no entry can drop (an empty table, or every weight 0).
 * Shares are each weight over the table's total, so a forge builder's partly filled table reads as
 * the table it would be at those proportions; a published table's weights add up to 1,000,000,
 * where this is boxed's arithmetic.
 *
 * "Worth at least the EV" is compared in integers, `value × total ≥ Σ weight × value`, rather than
 * against a floating-point EV: boxed's 1e-9 tolerance is too small for values in the millions of
 * cents, where a table of equal entries can find none at or above its own EV and read infinite.
 * Weights and values are integers, so both sides are exact while they stay under 2^53 (a weight
 * total of 1,000,000 against values under $90M).
 */
export function calculateVolatility(entries: readonly VolatilityEntry[]): number | null {
  const weightOf = (entry: VolatilityEntry) => Math.max(0, entry.weight);
  const total = entries.reduce((sum, entry) => sum + weightOf(entry), 0);
  if (total <= 0) return null;
  // The EV scaled by the total weight: Σ weight × value.
  const scaledEv = entries.reduce((sum, entry) => sum + weightOf(entry) * entry.value, 0);
  const atOrAboveEv = entries
    .filter((entry) => entry.value * total >= scaledEv)
    .reduce((sum, entry) => sum + weightOf(entry), 0);
  // The most valuable entry that can drop is worth at least the EV, so this is above 0 whenever
  // something can drop; the guard keeps a divide by zero from ever storing Infinity.
  return atOrAboveEv > 0 ? total / atOrAboveEv : null;
}

/** A value read against the scale. */
export interface VolatilityReading {
  level: VolatilityLevel;
  /** The level's place, 0 (low) to 3 (max). */
  band: number;
  /** Where in its level the value sits, 0 its start and 1 its end: a meter's marker. */
  position: number;
  /** The value clamped to 0..max. */
  value: number;
}

/**
 * `value` on a scale from 0 to `max`, or null when there is nothing to show: no value, not a
 * number, negative (boxed's −1 means unrated), or a scale that isn't positive. Above `max` reads
 * as `max`. Levels are quarters, and a value on a level's edge starts the next one: [0, 4) low,
 * [4, 8) medium, [8, 12) high, [12, 16] max on the default scale (boxed's filter ranges).
 */
export function readVolatility(
  value: number | null | undefined,
  max: number = VOLATILITY_MAX,
): VolatilityReading | null {
  if (value === null || value === undefined || !Number.isFinite(value) || value < 0) return null;
  if (!Number.isFinite(max) || max <= 0) return null;
  const clamped = Math.min(value, max);
  const scaled = (clamped / max) * VOLATILITY_LEVELS.length;
  const band = Math.min(VOLATILITY_LEVELS.length - 1, Math.floor(scaled));
  return {
    level: VOLATILITY_LEVELS[band] ?? "low",
    band,
    position: Math.min(1, scaled - band),
    value: clamped,
  };
}
